import { useCallback } from 'react'
import Button from 'cc/Button'
import toast from 'react-hot-toast'
import { cn, handle } from 'common/utils'
import TimeAgo from 'cc/TimeAgo'
import Link from 'components/Link'
import Tooltip from 'cc/Tooltip'
import ConditionalWrap from 'cc/ConditionalWrap'
import bext from 'utils/bext'
import { useExtensionListener } from 'utils/hooks'
import routes from 'common/routes'

import { startFollowingUser, stopFollowingUser } from 'common/siteApi'
import { useHasMounted } from 'common/hooks'

const FollowButton = ({
  user,
  uid,
  doesFollow,
  showAsFollowing,
  setShowAsFollowing,
  includeHandle,
  hideIfFollowing,
  className,
}) => {
  useExtensionListener(
    uid,
    'startedFollowing',
    useCallback(
      (followedUid) => {
        console.log('useExtensionListener: startedFollowing', followedUid)
        if (followedUid === user?.id) setShowAsFollowing(true)
      },
      [user, setShowAsFollowing],
    ),
  )

  const followUser = useCallback(async () => {
    setShowAsFollowing(true)
    await toast.promise(startFollowingUser(user.id), {
      loading: 'Following...',
      success: (data) => {
        bext.refreshFollowedUsers()
        return (
          <span>
            Now following{' '}
            <Link href={routes.userPath(user)}>
              <strong>{handle(user, true)}</strong>
            </Link>
          </span>
        )
      },
      error: (error) => {
        console.log('[ERROR] following user', error?.message, error)
        setShowAsFollowing(false)
        return <span>Unable to follow: {error?.message || 'server error'}</span>
      },
    })
  }, [user, setShowAsFollowing])

  const unfollowUser = useCallback(async () => {
    setShowAsFollowing(false)
    await toast.promise(stopFollowingUser(user.id), {
      loading: 'Unfollowing...',
      success: (data) => {
        bext.refreshFollowedUsers()
        return (
          <span>
            No longer following{' '}
            <Link href={routes.userPath(user)}>
              <strong>{handle(user, true)}</strong>
            </Link>
          </span>
        )
      },
      error: (error) => {
        console.log('[ERROR] following user', error?.message, error)
        setShowAsFollowing(false)
        return (
          <span>Unable to unfollow: {error?.message || 'server error'}</span>
        )
      },
    })
  }, [user, setShowAsFollowing])

  const toggleFollowing = useCallback(() => {
    showAsFollowing ? unfollowUser() : followUser()
  }, [showAsFollowing, followUser, unfollowUser])
  const hasMounted = useHasMounted()

  if (showAsFollowing === undefined) return null // wait until we get a response back to show a button either way
  if (!hasMounted || uid === user?.id) return null // wait until we get a response back to show a button either way
  if (hideIfFollowing && showAsFollowing) return null

  return (
    <Button
      className={className}
      onClick={toggleFollowing}
      variant={showAsFollowing ? 'secondary' : 'primary'}
    >
      {showAsFollowing ? 'Un-Follow' : 'Follow'}
      {includeHandle && ` ${handle(user)}`}
    </Button>
  )
}

export default FollowButton
